import { useCallback, useMemo, useState } from 'react'
import type { Chart } from 'klinecharts'
import { useIsPhone } from '@/hooks/useIsPhone'
import useBacktestChartState from '../../bot/hooks/useBacktestChartState'
import useScrubProjection from '../../bot/hooks/useScrubProjection'
import BacktestChart from '../../bot/components/BacktestChart'
import { precisionFromPointSize } from '../../bot/adapters/symbolsFetcher'
import { WorkbenchReplayBar } from './WorkbenchReplayBar'
import WalkthroughTradesPane from './WalkthroughTradesPane'
import useWalkthroughDrawingsOverlay from './useWalkthroughDrawingsOverlay'
import useWalkthroughIndicators from './useWalkthroughIndicators'
import useWalkthroughOrders from './useWalkthroughOrders'
import {
  barIntervalMs,
  buildsFormingBar,
  clampPace,
  DEFAULT_DETAIL,
  DEFAULT_PACE,
  engineSpeed,
  paceForDetail,
  type ReplayDetail,
} from './walkthroughPacing'
import type { DragOffset } from './WorkbenchFloatingBits'
import type { WalkthroughRun } from './useWalkthroughSession'
import type { SavedDrawing, SymbolSliceState } from './workbenchState'

const DEFAULT_PRECISION = 5

/**
 * Sub-bar animation is decided by the DETAIL control, not by the speed.
 *
 * The Backtest tab's policy is a set of slow speeds (TICK_REPLAY_SPEEDS), because there
 * speed is the only control. Here the two questions are separate — "how finely" is detail
 * and "how fast" is pace — so once detail says the bar should build, every pace it offers
 * should build it. Passing every speed makes the engine's membership test a no-op and
 * leaves the decision entirely with `formingBarEnabled`.
 */
const EVERY_SPEED: ReadonlySet<number> = new Set([0.5, 1, 2, 5, 10, 25, 50])

/**
 * Bar widths of blank chart held right of the newest bar.
 *
 * The replay reveals its candles at the right edge, and with no run-off each one arrives
 * pressed against the price axis — the reader sees it only after it has already stopped
 * moving. A few bars of space give the eye somewhere to follow it into. Small on purpose: it
 * is taken off the left of the same window, so a big margin would cost real history.
 */
const REPLAY_RIGHT_PAD_BARS = 5

export interface WorkbenchWalkthroughProps {
  run: WalkthroughRun
  /** Serialized drawings, frozen when the mode was entered. */
  drawings: SavedDrawing[]
  /** Indicators to mirror onto the replay chart. */
  indicators: SymbolSliceState['indicators']
  /**
   * Where the user dragged the floating bar to — owned by the workbench, because the
   * position has to survive this subtree unmounting, which it does on every rebuild.
   * Passed flat rather than as the hook's result object: that object hands out ref
   * callbacks, and reading properties off it during render reads as a mid-render ref
   * access to the compiler.
   */
  barDrag?: DragOffset
  onBarDragChange?: (d: DragOffset) => void
  onBarDragReset?: () => void
  barRef?: (el: HTMLElement | null) => void
}

/**
 * The Walkthrough mode's read-only replay: the generated code trading the period the user
 * drew on, with their own drawings revealing alongside the real orders.
 *
 * Everything about playback — tick streaming, windowed candle loading, viewport clamping,
 * cursor persistence — comes from useBacktestChartState. This component adds what the
 * Backtest tab has no use for: the drawings layer, the pips-only trade pane, the live order
 * lines, and a transport that speaks in detail-plus-pace rather than in engine speeds.
 *
 * Read-only comes structurally, not by disabling things: there is simply no drawing rail, no
 * tool state and no pointer handlers on this subtree.
 */
export default function WorkbenchWalkthrough({
  run,
  drawings,
  indicators,
  barDrag,
  onBarDragChange,
  onBarDragReset,
  barRef,
}: WorkbenchWalkthroughProps) {
  // The raw engine, so the drawings and indicators can be painted onto the same chart the
  // replay drives. BacktestChart's imperative handle covers replay only.
  const [chart, setChart] = useState<Chart | null>(null)
  const isPhone = useIsPhone()

  // ---- Pacing --------------------------------------------------------------------------
  const [detail, setDetail] = useState<ReplayDetail>(DEFAULT_DETAIL)
  const [pace, setPace] = useState(DEFAULT_PACE)
  const speed = engineSpeed(detail, pace)
  const barInterval = barIntervalMs(detail, run.timeframe, pace)

  const handleDetailChange = useCallback((next: ReplayDetail) => {
    setDetail(next)
    // Not a clamp: entering tick always lands on its own default, because a pace carried
    // across from candle would mean something else entirely there. See paceForDetail.
    setPace((prev) => paceForDetail(next, prev))
  }, [])

  const handlePaceChange = useCallback(
    (next: number) => setPace(clampPace(detail, next)),
    [detail],
  )

  const {
    candles,
    orders,
    isLoading,
    loadError,
    chartRef,
    maxAvailableTs,
    progressViewPosition,
    progressViewTs,
    isPlaying,
    isPlayDisabled,
    handlePlay,
    handlePause,
    handleStep,
    handleVisibleRangeChange,
    handleChartUserNavigate,
    handleEquityTimeClick,
    handleShowTime,
  } = useBacktestChartState({
    btid: run.btid,
    symbol: run.symbol,
    initialTimeframe: run.timeframe,
    initialStatus: run.status,
    initialProgressBacktest: run.progressBacktest,
    initialProgressView: run.progressView,
    // The DISPLAY span, not the run's real range. The server pads the period with indicator
    // warm-up and rounds it to whole UTC days, and these two values are what bounds candle
    // loading, paging and the viewport — left as the real range they put hundreds of bars of
    // nothing on either side of the drawings and spend most of the progress bar getting there.
    rangeStartTs: run.displayStartTs,
    rangeEndTs: run.displayEndTs,
    // Controlled, not initial: the detail-plus-pace pair is the source of truth, and it
    // changes while the run is playing.
    speed,
    replayStartTs: run.replayStartTs,
    // 0% is where the WATCHING starts, which is a screenful inside displayStartTs: the chart
    // holds that screen as static context so the opening frame reads as a chart rather than as
    // ten stretched candles, and the transport must not count it — measuring from the chart's
    // own left edge is what used to open a fresh run part-way along.
    progressWindowStartTs: run.replayStartTs,
    formingBarEnabled: buildsFormingBar(detail),
    tickReplaySpeeds: EVERY_SPEED,
    barIntervalMsOverride: barInterval,
    // A RUN THE USER JUST WAITED FOR STARTS ITSELF. Preparing a walkthrough takes long enough to
    // look away from, and the reward for coming back should not be another button: the chart
    // appearing IS the answer to "is it ready?", so it plays.
    //
    // Only for a run this session had built (`reused` false) and that has never been watched.
    // Re-entering a walkthrough is the opposite intent — the user came back to a cursor they
    // parked, usually to look at one bar — and starting there would run the replay away from
    // whatever they came to see.
    autoPlayOnReady: !run.reused && run.progressView == null,
    // A WALKTHROUGH IS WATCHED, NOT SCANNED. Scrolling back to check a level, or zooming in on
    // the bar an entry fired on, is part of watching — so it neither stops the run nor gets
    // undone by it: the replay keeps the user's zoom, and hands them the viewport for as long as
    // they are looking away from the playhead. The run stops on Pause, on a scrub, on a trade
    // row, and at the end.
    pauseOnChartNavigation: false,
  })

  // The run's own trades, fetched once and numbered once. Two readers below — the pane's rows
  // and the chart's order tooltip — and they have to agree on both which trades exist and what
  // each one is called.
  const { orders: runOrders, tickets: orderTickets } = useWalkthroughOrders({
    btid: run.btid,
    rangeStartTs: run.rangeStartTs,
    rangeEndTs: run.rangeEndTs,
    displayStartTs: run.displayStartTs,
    displayEndTs: run.displayEndTs,
  })

  // Same window as the progress readout, so the scrubber's own zero and the bar's 0% are the
  // same instant — and so dragging to either end lands where the run actually starts and
  // stops rather than somewhere in the padding the transport cannot express.
  const scrub = useScrubProjection({
    rangeStartTs: run.replayStartTs,
    rangeEndTs: run.displayEndTs,
    maxAvailableTs,
    progressViewPosition,
    onScrubTime: handleEquityTimeClick,
  })

  // Same triple BacktestChart uses to detect a series change, so the drawings reconcile picks
  // up pagination without the chart having to tell us.
  const dataKey = candles.length
    ? `${candles[0].time}:${candles[candles.length - 1].time}:${candles.length}`
    : ''

  useWalkthroughDrawingsOverlay({
    chart,
    drawings,
    revealTs: progressViewTs,
    dataKey,
  })

  useWalkthroughIndicators(chart, indicators)

  const bc = run.configInput?.backtest_config ?? {}
  const pipSize = typeof bc.pip_size === 'number' ? bc.pip_size : 0.0001
  const precision =
    precisionFromPointSize(typeof bc.point_size === 'number' ? bc.point_size : null)
    ?? DEFAULT_PRECISION

  // Floating P/L on open trades is measured against the newest revealed bar — the same
  // boundary the chart draws to, so the pane and the chart never disagree.
  const markPrice = candles.length > 0 ? candles[candles.length - 1].close : null

  const handleTogglePlay = useCallback(() => {
    if (isPlaying) {
      handlePause()
      return
    }
    if (isPlayDisabled) return
    handlePlay()
  }, [isPlaying, isPlayDisabled, handlePlay, handlePause])

  const replayBar = useMemo(
    () => ({
      playing: isPlaying,
      pace,
      onPaceChange: handlePaceChange,
      detail,
      onDetailChange: handleDetailChange,
      value: scrub.visibleCount,
      max: scrub.totalCandles,
      maxScrubIndex: scrub.maxScrubIndex,
      onScrub: scrub.handleScrub,
      onTogglePlay: handleTogglePlay,
      onStepBack: () => handleStep(-1),
      onStepForward: () => handleStep(1),
    }),
    [
      isPlaying, pace, handlePaceChange, detail, handleDetailChange,
      scrub.visibleCount, scrub.totalCandles, scrub.maxScrubIndex, scrub.handleScrub,
      handleTogglePlay, handleStep,
    ],
  )

  return (
    <div className="absolute inset-0 flex flex-col bg-card">
      <div className="relative flex-1 min-h-0">
        <BacktestChart
          ref={chartRef}
          candles={candles}
          orders={orders}
          symbol={run.symbol}
          timeframe={run.timeframe}
          autoSetInitialVisibleRange={false}
          boundPanToData
          rightPadBars={REPLAY_RIGHT_PAD_BARS}
          maxAvailableTimestamp={maxAvailableTs ?? undefined}
          onVisibleRangeChange={handleVisibleRangeChange}
          onUserNavigate={handleChartUserNavigate}
          onChartReady={setChart}
          // No volume in a walkthrough, deliberately: the question here is "does the code
          // fire where I drew?", and a volume histogram answers a different one. The user's
          // own volume preference on the canvas is untouched.
          showVolume={false}
          liveOrderLines
          pipSize={pipSize}
          // So the card a marker opens names the trade the same way its row does.
          orderTickets={orderTickets}
        />

        {/* Desktop only. On a wide screen the bar still floats inside the chart pane, where
            it can be dragged out of the way of a trade. On a phone it renders as its own row
            below this box, so it can sit between the chart and the trades pane rather than on
            top of the price axis. */}
        {!isPhone && (
          <WorkbenchReplayBar
            {...replayBar}
            drag={barDrag}
            onDragChange={onBarDragChange}
            onDragReset={onBarDragReset}
            barRef={barRef}
          />
        )}

        {/* The mode frame: a whitish hairline round the chart surface, the same tone the
            phone band's own top edge wears, so the two read as one mode rather than as
            generic chart furniture. With no dim around it, the frame carries the "you are in
            a mode, Exit to leave" message on its own. */}
        <div className="pointer-events-none absolute inset-0 z-[100] border border-foreground/30" />

        {loadError && (
          <div className="absolute inset-x-0 top-24 z-[110] flex justify-center">
            <span className="rounded-md border border-border bg-popover px-3 py-1.5 text-caption text-danger shadow-popover">
              {loadError}
            </span>
          </div>
        )}
        {!loadError && isLoading && candles.length === 0 && (
          <div className="absolute inset-x-0 top-24 z-[110] flex justify-center">
            <span className="rounded-md border border-border bg-popover px-3 py-1.5 text-caption text-muted-foreground shadow-popover">
              Loading candles…
            </span>
          </div>
        )}
      </div>

      {/*
        THE PHONE'S BAND IS A ROW OF THIS COLUMN, NOT AN OVERLAY ON THE CHART.
        It has to sit in the space between the chart and the trades pane, overlapping
        neither — and there WAS no such space: the chart fills its `flex-1` box right down to
        the pane, with its own time axis on the last rows, which is exactly what an
        overlaid bar covered. The space has to be MADE, and the honest place to make it is
        here, in the element that owns how the height is divided. As a sibling row the band
        takes its own height, the chart flexes down by exactly that, and the axis stays fully
        visible with no dead gap either — the band IS the gap.

        Two things fall out of it, both wanted: the mode frame above no longer draws its
        bottom edge across the band, and a press on the band cannot bubble into the chart
        pane's own gesture handling.
      */}
      {isPhone && <WorkbenchReplayBar {...replayBar} />}

      <WalkthroughTradesPane
        orders={runOrders}
        playheadTs={progressViewTs}
        markPrice={markPrice}
        pipSize={pipSize}
        precision={precision}
        // Scrolls the chart to the trade and leaves the playhead alone — the replay keeps its
        // place, and see handleShowTime for why moving the cursor would also rewrite the very
        // list the click came from.
        onShowTrade={handleShowTime}
        // The cursor has run out of data to reveal, so the run is over as far as the user is
        // concerned. Lands the pane on Closed rather than on an empty Open tab.
        atEnd={isPlayDisabled}
      />
    </div>
  )
}
